import type { BankItem } from "../../engine/bank-types";

/**
 * 觀點衝突 — passages that pull against each other.
 *
 * The Analects is not a consistent treatise, and pretending otherwise is
 * itself a misreading. These items put two passages in tension and ask the
 * student to say what kind of tension it is: a real limit, a difference of
 * occasion, a difference of speaker, or a genuine unresolved conflict.
 */
export const CONFLICT_ITEMS: BankItem[] = [
  {
    id: "cf-1",
    type: "conflict",
    refs: [410, 383],
    concepts: ["ren", "yili"],
    difficulty: 3,
    cognitive: "evaluate",
    purpose: "與《老子》以德報怨的正面對立",
    stem: "「以德報怨」是當時流行的主張，孔子答「何以報德？以直報怨，以德報德」。他反對的理由是？",
    options: [
      { id: "a", text: "以德報怨，則報德無以加之" },
      { id: "b", text: "他主張報復，認為怨必須還以怨", why: "「直」是公正處置，不是還以怨；他保留了「以德報德」。" },
      { id: "c", text: "他認為怨恨應當忘掉，不必回應", why: "「以直報怨」正是要有所回應，只是回應以公。" },
      { id: "d", text: "他認為德只能施於君主", why: "章中並無這樣的身分限定，說的是一般的待人之道。" },
    ],
    answerId: "a",
    explanation: "這是一次針對流行主張的正面反駁，論證方式是指出它的後果：把所有回應拉平，恩與怨就再無分別。孔子護的是分別本身。",
  },
  {
    id: "cf-2",
    type: "conflict",
    refs: [364, 84],
    concepts: ["xiao", "yili"],
    difficulty: 3,
    cognitive: "evaluate",
    purpose: "親情與公義的張力",
    stem: "13.18「父為子隱，子為父隱」與公義的要求之間存在張力。最恰當的描述是？",
    options: [
      { id: "a", text: "真實的兩難，孔子擇人倫真情一端" },
      { id: "b", text: "沒有張力，孔子的主張與公義完全一致", why: "葉公的立場正代表公義一端，兩者確有衝突，抹平它就讀不出這章的分量。" },
      { id: "c", text: "沒有張力，因為古代沒有法律", why: "葉公所述正是舉發制度，法律問題確實存在。" },
      { id: "d", text: "孔子其實贊成舉發，只是說得委婉", why: "「吾黨之直者異於是」是明確的不同意。" },
    ],
    answerId: "a",
    explanation: "讀出張力，比替古人消除張力更誠實。這一章之所以兩千年爭論不休，正因為雙方各據一端，而孔子選了他認為更根本的那一端。",
  },
  {
    id: "cf-3",
    type: "conflict",
    refs: [197, 459],
    concepts: ["zheng", "jiaoxue"],
    difficulty: 3,
    cognitive: "evaluate",
    purpose: "疑難章與明確主張的衝突處理",
    stem: "8.9「民可使由之，不可使知之」與 15.39「有教無類」表面衝突。較恰當的處理是？",
    options: [
      { id: "a", text: "以反覆出現者為主，孤證存疑" }, { id: "e", text: "以最早成篇的一章為準，其餘從之", why: "《論語》各篇的成篇先後本身就無定論，無法充當判準。" },
      { id: "b", text: "以 8.9 為準，判定孔子主張愚民，「有教無類」只是口號", why: "有教無類有 7.7、7.29 等實踐記載支持，8.9 則連斷句都無定論。" },
      { id: "c", text: "兩句都不必當真，古書多有矛盾", why: "放棄判斷不是方法；證據強弱本身是可以評估的。" },
      { id: "d", text: "把 8.9 改讀成與 15.39 一致，衝突自然消失", why: "為求一致而改讀，正是「毋意毋必」所戒。" },
    ],
    answerId: "a",
    explanation: "遇到衝突，先問哪一邊證據更強。這比急著調和或急著定罪都更接近讀書的本分——也承認有些問題暫時就是懸而未決。",
  },
  {
    id: "cf-4",
    type: "conflict",
    refs: [392, 62],
    concepts: ["ren", "li"],
    difficulty: 3,
    cognitive: "evaluate",
    purpose: "同一人不同面向的評價衝突",
    stem: "孔子說管仲「不知禮」，又說他「如其仁」。有人據此說孔子自相矛盾。最好的回應是？",
    options: [
      { id: "a", text: "分項評價：禮不合格而功業許其仁" },
      { id: "b", text: "確實矛盾，說明孔子沒有一貫標準", why: "5.15 評孔文子同樣是分項處理，可見這是他一貫的方法。" },
      { id: "c", text: "「如其仁」是反話", why: "上下文在駁子路、子貢質疑管仲不死，語意明確為肯定。" },
      { id: "d", text: "其中一章必為後人所竄改", why: "並無版本依據支持這樣的判斷，是為消解矛盾而作的假設。" },
    ],
    answerId: "a",
    explanation: "一個人可以在某一面不合格而在另一面極有貢獻。孔子從不用單一評價蓋棺——這既是論人的技術，也是一種公道。",
  },
  {
    id: "cf-5",
    type: "conflict",
    refs: [501, 459],
    concepts: ["zhiren", "jiaoxue"],
    difficulty: 3,
    cognitive: "evaluate",
    purpose: "與今日價值的衝突如何面對",
    stem: "17.25 與今日的平等觀念直接衝突。作為讀者，較負責任的態度是？",
    options: [
      { id: "a", text: "承認衝突與局限，也說明訓釋分歧" },
      { id: "b", text: "以今日標準全盤否定《論語》", why: "以單章否定全書，會連同其餘可用的部分一併丟棄。" },
      { id: "c", text: "堅持古人必有深意，一定不是歧視", why: "先設定結論再找解釋，是「毋意毋必」所戒的做法。" },
      { id: "d", text: "略過不談，只讀能接受的章句", why: "選擇性閱讀會讓人失去對文本整體的判斷力。" },
    ],
    answerId: "a",
    explanation: "讀古典遲早會遇到這種時刻。真正的訓練不在於為古人辯護或給古人定罪，而在於能同時說出「它確實這麼寫」與「我不同意」。",
  },
  {
    id: "cf-6",
    type: "conflict",
    refs: [508, 414],
    concepts: ["chushi"],
    difficulty: 3,
    cognitive: "evaluate",
    purpose: "隱者與孔子的路線之爭",
    stem: "隱者說「滔滔者天下皆是也，而誰以易之」，孔子說「天下有道，丘不與易也」。這場分歧的實質是？",
    options: [
      { id: "a", text: "同一前提，卻得出相反的行動結論" },
      { id: "b", text: "兩人對天下是否無道的判斷不同", why: "孔子並不否認天下無道，這正是他行動的前提。" },
      { id: "c", text: "隱者不了解孔子的主張", why: "他們對孔子知之甚詳，長沮還說「是知津矣」。" },
      { id: "d", text: "孔子認為隱者是錯的，必須駁斥", why: "他「憮然」而歎，語氣中有理解，未作駁斥。" },
    ],
    answerId: "a",
    explanation: "最好的思想衝突是這樣的：前提相同，結論相反。因此無法靠澄清事實來解決——它逼你亮出自己的選擇。",
  },
  {
    id: "cf-7",
    type: "conflict",
    refs: [497],
    concepts: ["xiao", "li"],
    difficulty: 3,
    cognitive: "evaluate",
    purpose: "宰我之問是否有理",
    stem: "宰我認為三年之喪太久，理由是「君子三年不為禮，禮必壞；三年不為樂，樂必崩」。這個理由本身如何評價？",
    options: [
      { id: "a", text: "有力：以孔子所重之禮樂反證長喪" },
      { id: "b", text: "完全無理，只是為自己貪圖安逸找藉口", why: "孔子最後責他不仁，針對的是他答「安」，而非這條論證本身不成立。" },
      { id: "c", text: "有理，所以孔子接受了他的主張", why: "孔子並未接受，只說「女安則為之」。" },
      { id: "d", text: "這個理由與喪期無關，屬於答非所問", why: "它正面論證了長喪的代價，是切題的。" },
    ],
    answerId: "a",
    explanation: "承認對手的論證有力，才談得上真正的爭論。孔子沒有從這條路反駁他，而是換了戰場——問他心安不安。這一轉才是關鍵。",
  },
  {
    id: "cf-8",
    type: "conflict",
    refs: [479, 478],
    concepts: ["xue", "jiaoxue"],
    difficulty: 3,
    cognitive: "evaluate",
    purpose: "相鄰兩章的張力",
    stem: "17.2「性相近也，習相遠也」與 17.3「唯上知與下愚不移」緊鄰。兩者的關係最好理解為？",
    options: [
      { id: "a", text: "前者是通則，後者是兩端的例外" },
      { id: "b", text: "後者推翻了前者，孔子最終認為天賦決定一切", why: "若如此，他辦學授徒與「有教無類」都失去根據。" },
      { id: "c", text: "兩章毫無關係，只是編排相鄰", why: "同論人性與變化，主題相連。" },
      { id: "d", text: "兩章完全一致，沒有張力", why: "一個強調可變，一個強調不移，張力是實在的。" },
    ],
    answerId: "a",
    explanation: "通則與例外並存，是《論語》常見的結構。要注意的是分量：孔子把「習相遠」說成普遍情形，把「不移」限定在兩端——例外沒有吃掉規則。",
  },
  {
    id: "cf-9",
    type: "conflict",
    refs: [69, 448],
    concepts: ["ren", "zhiren"],
    difficulty: 3,
    cognitive: "evaluate",
    purpose: "仁者有好惡與不輕下判斷",
    stem: "4.3「唯仁者能好人，能惡人」要求有明確好惡；15.28「眾惡之，必察焉」要求不輕信。兩者如何並存？",
    options: [
      { id: "a", text: "不衝突：好惡要有，但須自己查證" },
      { id: "b", text: "衝突：一個要表態，一個要中立", why: "「必察」不是中立，是查證後再表態。" },
      { id: "c", text: "並存不了，孔子在此前後不一", why: "兩章分別針對好惡的正當性與其形成過程，層面不同。" },
      { id: "d", text: "4.3 只適用於仁者，15.28 適用於常人", why: "章中無此分工，且仁者更應查證。" },
    ],
    answerId: "a",
    explanation: "把兩章接起來，就得到一條完整的規則：要有好惡（否則是鄉愿），但好惡必須經過自己的查證（否則是隨眾）。缺任何一半都會出問題。",
  },
  {
    id: "cf-10",
    type: "conflict",
    refs: [201, 397],
    concepts: ["chushi", "zheng"],
    difficulty: 3,
    cognitive: "evaluate",
    purpose: "隱與不敢不告的並存",
    stem: "8.13 主張「邦無道則隱」，14.21 孔子卻在無位時仍沐浴而朝請討陳恆。這兩者如何統一？",
    options: [
      { id: "a", text: "隱的是身而非言，大事仍不敢不告" },
      { id: "b", text: "無法統一，孔子言行不一致", why: "隱指的是仕與祿的取捨，與盡言的責任不在同一層面。" },
      { id: "c", text: "8.13 只是理論，孔子自己並不實行", why: "他確實多次去國、辭仕，並非空言。" },
      { id: "d", text: "14.21 時魯國有道，故不適用 8.13", why: "當時魯政在三桓，難言有道；且他請討被拒。" },
    ],
    answerId: "a",
    explanation: "「以吾從大夫之後，不敢不告也」——身分已退，責任未退。隱的是身，不是言。這一區分讓「隱」不至於淪為卸責的藉口。",
  },
  {
    id: "cf-11",
    type: "conflict",
    refs: [481, 483],
    concepts: ["chushi", "yili"],
    figures: ["zilu"],
    difficulty: 3,
    cognitive: "evaluate",
    purpose: "子路兩度質疑孔子欲往",
    stem: "公山弗擾、佛肸兩度召孔子，孔子欲往，子路兩次以孔子自己的話質疑他。孔子的回應是？",
    options: [
      { id: "a", text: "吾豈匏瓜也哉，焉能繫而不食" },
      { id: "b", text: "否認自己說過那些話", why: "他未否認，且以「然，有是言也」承認。" },
      { id: "c", text: "斥責子路無禮，不再理會", why: "他認真作了回答，並自陳心跡。" },
      { id: "d", text: "承認錯誤，放棄前往", why: "章中未記他認錯；記的是他的辯解。" },
    ],
    answerId: "a",
    explanation: "《論語》連這種難堪也不刪：學生拿老師的話堵老師，而老師的回答坦白得近乎孤注。這兩章讓孔子從聖像變回一個著急的人。",
  },
  {
    id: "cf-12",
    type: "conflict",
    refs: [162, 166],
    concepts: ["yili"],
    difficulty: 2,
    cognitive: "evaluate",
    purpose: "求富與輕富的並存",
    stem: "7.12「富而可求也，雖執鞭之士，吾亦為之」與 7.16「不義而富且貴，於我如浮雲」是否矛盾？",
    options: [
      { id: "a", text: "不矛盾：後者限定在「不義而」三字" },
      { id: "b", text: "矛盾：一個要富，一個不要富", why: "7.16 的主語是「不義而富且貴」，並非富貴本身。" },
      { id: "c", text: "不矛盾：兩句都在說孔子想發財", why: "7.12 隨即說「如不可求，從吾所好」，並非執著。" },
      { id: "d", text: "矛盾：反映孔子早晚年思想的變化", why: "無時間線索，且用一句限定即可消解，不必訴諸思想轉變。" },
    ],
    answerId: "a",
    explanation: "看似矛盾的兩句，關鍵往往在某個限定詞上。「不義而」三個字承擔了全部差別——讀漏它，孔子就成了前後不一的人。",
  },
  {
    id: "cf-13",
    type: "conflict",
    refs: [489, 370, 445],
    concepts: ["zhiren", "zhongyong"],
    difficulty: 3,
    cognitive: "evaluate",
    purpose: "毀譽標準的一致性",
    stem: "15.25「吾之於人也，誰毀誰譽？如有所譽者，其有所試矣」與 13.24 對鄉人好惡的態度，共同構成什麼標準？",
    options: [
      { id: "a", text: "評價須有所試，且不隨眾人好惡" },
      { id: "b", text: "不應該評價任何人", why: "「如有所譽者」表明他確實會稱譽，只是有條件。" },
      { id: "c", text: "應當以多數人的評價為準", why: "13.24 明確排除了這一點。" },
      { id: "d", text: "只能評價自己認識的人", why: "條件是「有所試」即有據可驗，不限於熟識。" },
    ],
    answerId: "a",
    explanation: "「其有所試矣」五個字定下一條硬規矩：稱讚一個人，得說得出你憑什麼。這在人人可以隨口評論的今天，門檻反而更高了。",
  },
  {
    id: "cf-14",
    type: "conflict",
    refs: [499, 190],
    concepts: ["yili", "li"],
    figures: ["zilu"],
    difficulty: 2,
    cognitive: "evaluate",
    purpose: "勇的價值需要條件",
    stem: "子路問「君子尚勇乎？」孔子答「君子義以為上。君子有勇而無義為亂，小人有勇而無義為盜」。這個回答的結構是？",
    options: [
      { id: "a", text: "不否定勇，而以義為其前提與分界" },
      { id: "b", text: "直接否定勇，認為君子不應尚勇", why: "他沒有否定勇，只是把它放在義之下。" },
      { id: "c", text: "認為只有君子可以有勇", why: "他明說小人也可能有勇，結果是為盜。" },
      { id: "d", text: "回答與問題無關，轉移了話題", why: "正是就「尚不尚勇」給出條件性的答案。" },
    ],
    answerId: "a",
    explanation: "問的是「要不要」，答的是「在什麼之下」。孔子處理德目衝突的典型手法：不做取捨題，而是重新排出層級。",
  },
  {
    id: "cf-15",
    type: "conflict",
    refs: [146],
    concepts: ["ren", "zhiren"],
    figures: ["zaiwo"],
    difficulty: 3,
    cognitive: "evaluate",
    purpose: "仁者是否應當不顧一切",
    stem: "宰我設難：「井有仁焉，其從之也？」孔子答「何為其然也？君子可逝也，不可陷也；可欺也，不可罔也」。這個回答的要點是？",
    options: [
      { id: "a", text: "可逝而不可陷：行仁不排除判斷力" },
      { id: "b", text: "仁者遇到危險應當立刻迴避", why: "「可逝也」正是可以前往，並未主張迴避。" },
      { id: "c", text: "仁者應當不顧一切地跳下去救人", why: "「不可陷也」明確排除自陷。" },
      { id: "d", text: "宰我的問題很無聊，孔子拒絕回答", why: "他正面作答，且答得極有分寸。" },
    ],
    answerId: "a",
    explanation: "宰我的設難很刁：如果仁必須救人，那騙你跳井你跳不跳。孔子的回答把仁與愚勇分開——可以被感動，不可以被玩弄。",
  },
  {
    id: "cf-16",
    type: "conflict",
    refs: [88, 461],
    concepts: ["xin", "junzi"],
    difficulty: 2,
    cognitive: "evaluate",
    purpose: "慎言與辭達的並存",
    stem: "孔子既說「古者言之不出，恥躬之不逮也」（慎言），又說「辭達而已矣」。兩者的關係是？",
    options: [
      { id: "a", text: "一管說不說，一管怎麼說，各管一段" },
      { id: "b", text: "衝突：一個要少說，一個要說清楚", why: "說清楚與少說不對立；慎言針對的是輕率承諾。" },
      { id: "c", text: "兩者都主張不必說話", why: "「辭達」正要求把意思說到位。" },
      { id: "d", text: "「辭達而已矣」是說話越少越好", why: "「達」是達意的標準，不是數量標準。" },
    ],
    answerId: "a",
    explanation: "孔子論言的兩條線很清楚：出口前要掂量能不能做到，出口後只求把意思送到。既不鼓勵沉默，也不欣賞辭藻。",
  },
];
